import React, { Fragment, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ButtonToolbar, Content, Icon, Loader } from 'rsuite';
import { Redirect, useParams } from 'react-router-dom';
import { get, isEmpty, pick } from 'lodash/fp';
import { MODULE as GUEST_MODULE } from '../../../constants/permissions/guest';
import { viewAccess } from '../../../helpers/permission';
import pagePathToUrl from '../../../helpers/pagePathToUrl';
import fullNameFormat from '../../../helpers/formatFullName';
import {
  fetchGuestVisitTrack,
  fetchSettings,
  fetchVisitColumns,
  fetchVisitConfigs
} from '../../../redux/sagas/routines';
import {
  getIsFetched,
  getVisitTrackViewData
} from '../../../redux/Guest/selectors/routes/visit-track-view';
import PageLayout from '../../../components/guest/PageLayout';
import AccessDenied from '../../../components/AccessDenied';
import AccessControl from '../../../components/AccessControl';
import CustomBreadcrumb from '../../../components/Breadcrumb';
import { guestsBasePath, guestsViewPath } from '../../Paths';
import VisitDetails from '../../../components/guest/VisitDetails';
import { getAllVisitConfigs } from '../../../redux/selectors/visit/configs';
import { getVisitCustomFields } from '../../../redux/selectors/visit/columns';
import MainDetails from '../View/MainDetails';
import { getMainSettingsData } from '../../../redux/Settings/selectors/main';
import redirectTo from '../../../helpers/redirectTo';
import { createVisitUrl } from '../../../helpers/reservationUrls';

export default () => {
  const dispatch = useDispatch();
  const { id: guestId, visitId, visitTrackId } = useParams();
  const visitConfigs = useSelector(getAllVisitConfigs);
  const customFields = useSelector(getVisitCustomFields);
  const settings = useSelector(getMainSettingsData);
  const data = useSelector(getVisitTrackViewData);
  const guest = get('guestSnapshot', data);
  const isFetched = useSelector(getIsFetched);

  useEffect(
    () => {
      dispatch(fetchGuestVisitTrack({ id: visitId, visitTrackId }));
    },
    [visitId]
  );

  useEffect(
    () => {
      if (isEmpty(visitConfigs)) {
        dispatch(fetchVisitConfigs());
      }
    },
    [visitConfigs]
  );

  useEffect(
    () => {
      if (isEmpty(customFields)) {
        dispatch(fetchVisitColumns());
      }
    },
    [customFields]
  );

  useEffect(
    () => {
      if (isEmpty(settings)) {
        dispatch(fetchSettings());
      }
    },
    [settings]
  );

  const handleEdit = () => {
    redirectTo(
      createVisitUrl({
        id: visitId,
        guestId,
        outreachId: get('outreach_id', data)
      }),
      true
    );
  };

  if (isFetched && !data) {
    return <Redirect to={guestsBasePath} />;
  }

  return (
    <PageLayout>
      <AccessControl
        allowOnlyFor={viewAccess(GUEST_MODULE)}
        fallback={<AccessDenied />}
      >
        <CustomBreadcrumb
          items={[
            {
              path: guestsBasePath,
              label: `Guests`
            },
            {
              path: pagePathToUrl(guestsViewPath, {
                id: guestId,
                section: 'visits'
              }),
              label: `${fullNameFormat(
                pick(['firstname', 'middlename', 'lastname'], guest)
              )}'s Visits`
            },
            { label: 'View' }
          ]}
        />
        <Content>
          {!isFetched ? (
            <Loader center />
          ) : (
            <Fragment>
              <MainDetails guest={guest} settings={settings} isSnapshot />
              <h5>Visit Details</h5>
              <VisitDetails
                visitConfigs={visitConfigs}
                visitCustomFields={customFields}
                guestVisit={data}
              />
              <ButtonToolbar>
                <Button onClick={handleEdit} appearance="primary">
                  <Icon icon="pencil" /> Edit Visit
                </Button>
              </ButtonToolbar>
            </Fragment>
          )}
        </Content>
      </AccessControl>
    </PageLayout>
  );
};
